import { SHOWCASE_TYPES } from 'helpers/TypeHelper';
import {
  getAlternativeHeadline,
  getAvatarLogo,
  getBackgroundProps,
  getBadge,
  getButtons,
  getDescription,
  getEpisode,
  getEventDescription,
  getEventLogo,
  getEventStartTime,
  getFavoriteProps,
  getItemData,
  getLogo,
  getMetadata,
  getMobileInfoProps,
  getProgressBar,
  getPromoProps,
  getSeriesButtons,
  getV2SeriesButtons,
  getSeriesMetadata,
  getSeriesTuneIn,
  getShowcaseType,
  getTitle,
  getTrailerContext,
} from '../helpers/ProcessorHelper';

import { ANON_SERIES, CHANNEL, COLLECTION, AUTH_SERIES, EVENT } from '../index';

const { seriesDetailAnonymous_tuneIn: anonymous_tuneIn } = window.Language;

const ChannelProcessor = ({
  additionalFunctions,
  analytics,
  bookmarks,
  callCollectionImpressionEvent,
  panel,
}) => {
  const item = getItemData(panel);
  const showcaseType = getShowcaseType(item);
  const description = getDescription(item);
  const title = getTitle(item);
  const logoProps = getLogo(item);
  const alternativeHeadline = getAlternativeHeadline(item);
  const metaData = getMetadata(item, showcaseType);
  const progressProps = getProgressBar({ item, bookmarks });
  const badgeProps = getBadge(item);
  const buttonArray = getButtons({
    item,
    callCollectionImpressionEvent,
    analytics,
    additionalFunctions,
  });
  const backgroundProps = getBackgroundProps({ item, type: CHANNEL, buttons: buttonArray });
  const mobileInfoProps = getMobileInfoProps({ item, buttons: buttonArray });

  return {
    alternativeHeadline,
    backgroundProps,
    badgeProps,
    buttonArray,
    description,
    logoProps,
    metaData,
    mobileInfoProps,
    progressProps,
    title,
  };
};

const CollectionProcessor = ({
  additionalFunctions,
  analytics,
  bookmarks,
  callCollectionImpressionEvent,
  panel,
}) => {
  const item = getItemData(panel);
  const showcaseType = getShowcaseType(item);
  const description = getDescription(item);
  const title = getTitle(item);
  const logoProps = getLogo(item);
  const alternativeHeadline = getAlternativeHeadline(item);
  const metaData = getMetadata(item, showcaseType);
  const progressProps = getProgressBar({ item, bookmarks });
  const badgeProps = getBadge(item);
  const buttonArray = getButtons({
    item,
    callCollectionImpressionEvent,
    analytics,
    additionalFunctions,
  });
  const backgroundProps = getBackgroundProps({ item, type: COLLECTION, buttons: buttonArray });
  const mobileInfoProps = getMobileInfoProps({ item, buttons: buttonArray });

  return {
    alternativeHeadline,
    backgroundProps,
    badgeProps,
    buttonArray,
    description,
    hasCountdown: showcaseType === SHOWCASE_TYPES.SHOWCASE_COUNTDOWN,
    logoProps,
    metaData,
    mobileInfoProps,
    progressProps,
    title,
  };
};

const SeriesProcessor = ({
  additionalFunctions,
  analytics,
  bookmarks,
  callCollectionImpressionEvent,
  continueWatchingItems,
  favoriteProps,
  panel,
  playerVisibility,
}) => {
  const item = getItemData(panel);
  const episode = getEpisode({ item, continueWatchingItems });
  const descriptionProps = { item, episode };
  const title = getSeriesTuneIn(item, episode);
  const logoProps = getLogo(item);
  const metaData = getSeriesMetadata({ item, episode });
  const progressProps = getProgressBar({ item: episode, bookmarks });
  const playerProps = getTrailerContext({
    additionalFunctions,
    isAnonymous: false,
    item,
    playerVisibility,
  });
  const buttonArray = getSeriesButtons({
    item,
    episode,
    additionalFunctions,
    analytics,
    callCollectionImpressionEvent,
  });
  const favoriteData = getFavoriteProps({
    item,
    episode,
    favoritesManager: favoriteProps?.favoritesManager,
    favoritableItems: favoriteProps?.favoritableItems,
    recommendationId: favoriteProps?.recommendationId,
    additionalFunctions,
  });
  const backgroundProps = getBackgroundProps({ item, type: AUTH_SERIES, buttons: buttonArray });
  return {
    backgroundProps,
    buttonArray,
    descriptionProps,
    logoProps,
    metaData,
    favoriteData,
    progressProps,
    playerProps,
    title,
  };
};

const AnonymousSeriesProcessor = ({
  additionalFunctions,
  analytics,
  callCollectionImpressionEvent,
  hasTrailer,
  panel,
  playerVisibility,
  promos,
}) => {
  const item = getItemData(panel);
  const descriptionProps = { item };
  const title = anonymous_tuneIn;
  const logoProps = getLogo(item);
  const metaData = getSeriesMetadata({ item });
  const playerProps = getTrailerContext({
    additionalFunctions,
    isAnonymous: true,
    item,
    playerVisibility,
  });
  const buttonArray = getSeriesButtons({
    additionalFunctions,
    analytics,
    hasTrailer,
    callCollectionImpressionEvent,
    isAnonymous: true,
    item,
  });
  const backgroundProps = getBackgroundProps({ item, type: ANON_SERIES, buttons: buttonArray });
  const promoProps = getPromoProps({ item, promos });

  return {
    backgroundProps,
    buttonArray,
    descriptionProps,
    logoProps,
    metaData,
    playerProps,
    promoProps,
    title,
  };
};

const AnonymousSeriesProcessorV2 = ({
  activePlans,
  additionalFunctions,
  analytics,
  bookmarks,
  callCollectionImpressionEvent,
  continueWatchingItems,
  favoriteProps,
  panel,
  playerVisibility,
  showUpgradeCTA,
}) => {
  const item = getItemData(panel);
  const episode = getEpisode({ item, continueWatchingItems });
  const descriptionProps = { item, episode };
  const title = getSeriesTuneIn(item, episode);
  const logoProps = getLogo(item);
  const metaData = getSeriesMetadata({ item });
  const progressProps = getProgressBar({ item: episode, bookmarks });
  const playerProps = getTrailerContext({
    additionalFunctions,
    isAnonymous: false,
    item,
    playerVisibility,
  });
  const buttonArray = getV2SeriesButtons({
    activePlans,
    additionalFunctions,
    analytics,
    callCollectionImpressionEvent,
    episode,
    isAnonymous: true,
    item,
    showUpgradeCTA,
  });
  const favoriteData = getFavoriteProps({
    item,
    episode,
    favoritesManager: favoriteProps?.favoritesManager,
    favoritableItems: favoriteProps?.favoritableItems,
    recommendationId: favoriteProps?.recommendationId,
    additionalFunctions,
  });
  const backgroundProps = getBackgroundProps({ item, type: AUTH_SERIES, buttons: buttonArray });
  return {
    backgroundProps,
    buttonArray,
    descriptionProps,
    logoProps,
    metaData,
    favoriteData,
    progressProps,
    playerProps,
    title,
  };
};

const EventProcessor = ({
  panel,
  analytics,
  callCollectionImpressionEvent,
  additionalFunctions,
}) => {
  const item = getItemData(panel);
  const title = getTitle(item);
  const description = getEventDescription(item);
  const logoProps = getEventLogo(item);
  const buttonArray = getButtons({
    item,
    callCollectionImpressionEvent,
    analytics,
    additionalFunctions,
  });
  const backgroundProps = getBackgroundProps({ item, type: EVENT, buttons: buttonArray });
  const countdownTime = getEventStartTime(item);
  return {
    backgroundProps,
    buttonArray,
    description,
    countdownTime,
    logoProps,
    title,
  };
};

const PersonalityProcessor = ({ panel }) => {
  const title = panel?.name;

  const logoProps = getAvatarLogo(panel);
  return {
    logoProps,
    title,
  };
};
export {
  AnonymousSeriesProcessor,
  AnonymousSeriesProcessorV2,
  ChannelProcessor,
  CollectionProcessor,
  EventProcessor,
  SeriesProcessor,
  PersonalityProcessor,
};
